import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Play, Heart, Share2, MapPin, Calendar, Clock, Users, Layers, ExternalLink, Loader2, BookOpen, Eye } from 'lucide-react';
import { getCatalogueEpisode, getCatalogue, getEngage, engageView, engageLike } from '../services/cms';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../hooks/use-toast';

const BADGE_COLORS = ['#12b3a6', '#ed5c1f', '#f5a623', '#6d4aff', '#e0457b'];

// Formatage des compteurs (lecture seule, source Citytoon.org). Jamais de 0/mock.
const fmtStat = (n) => {
  if (n === null || n === undefined) return null;
  return new Intl.NumberFormat('fr-FR').format(n);
};

const AuthorAvatar = ({ author, size = 44 }) => (
  <div className="rounded-full overflow-hidden bg-primary/15 flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
    {author.portrait?.url
      ? <img src={author.portrait.url} alt={author.portrait.alt || `Portrait de ${author.name}`} loading="lazy" className="w-full h-full object-cover" />
      : <Users className="w-1/2 h-1/2 text-primary" />}
  </div>
);

const WebtoonFiche = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const collection = params.get('collection') || '';
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isFavorite, toggleFavorite, progress } = useFavorites();

  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [related, setRelated] = useState([]);
  const [engage, setEngage] = useState(null); // { engage_views_total, engage_likes_total, a_liked }
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    setData(null); setError(false); setEngage(null); window.scrollTo(0, 0);
    getCatalogueEpisode(slug, collection).then(setData).catch(() => setError(true));
  }, [slug, collection]);

  const ep = data?.episode;

  // Récupère les compteurs Engage (cumul local) une fois l'épisode connu.
  useEffect(() => {
    if (!ep?.source_id) return;
    getEngage(ep.source_id).then(setEngage).catch(() => setEngage(null));
  }, [ep?.source_id]);

  useEffect(() => {
    if (!ep) return;
    getCatalogue().then((list) => {
      const myColls = new Set((ep.collections || []).map((c) => c.slug));
      const myAuth = new Set((ep.auteurs || []).map((a) => a.slug));
      const scored = (list || [])
        .filter((e) => e.slug !== ep.slug && e.featured_image?.url)
        .map((e) => {
          const sameColl = (e.collections || []).some((c) => myColls.has(c.slug));
          const sameAuth = (e.auteurs || []).some((a) => myAuth.has(a.slug));
          return { e, score: (sameColl ? 2 : 0) + (sameAuth ? 1 : 0) };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, 5)
        .map((x) => x.e);
      setRelated(scored);
    }).catch(() => setRelated([]));
  }, [ep]);

  const collQS = collection ? `?collection=${encodeURIComponent(collection)}` : (data?.collection?.slug ? `?collection=${encodeURIComponent(data.collection.slug)}` : '');
  const readLink = ep ? `/toon/${encodeURIComponent(ep.slug)}${collQS}` : '#';
  const imagesCount = data?.images?.length || 0;
  const durationMin = useMemo(() => Math.max(1, Math.ceil(imagesCount * 0.5)), [imagesCount]);
  const year = ep?.published_at ? new Date(ep.published_at).getFullYear() : null;
  const isNew = ep?.published_at ? (Date.now() - new Date(ep.published_at).getTime()) < 90 * 24 * 3600 * 1000 : false;
  const started = ep && (progress?.[ep.source_id] || 0) > 0;

  // Cumul d'affichage = WordPress (lecture seule) + Engage (local). Jamais renvoyé vers WordPress.
  const wpViews = ep?.stats?.views ?? null;
  const wpLikes = ep?.stats?.likes ?? null;
  const engViews = engage?.engage_views_total ?? 0;
  const engLikes = engage?.engage_likes_total ?? 0;
  const aLiked = !!engage?.a_liked;
  const viewsDisplay = (wpViews !== null || engViews > 0) ? ((wpViews || 0) + engViews) : null;
  const likesDisplay = (wpLikes !== null || engLikes > 0) ? ((wpLikes || 0) + engLikes) : null;

  // Vue Engage : uniquement au clic sur « Commencer la lecture », fire-and-forget (n'entrave jamais la lecture).
  const countEngageView = () => {
    if (!ep?.source_id) return;
    engageView(ep.source_id).then((r) => {
      if (r?.counted) setEngage((e) => ({ ...(e || {}), engage_views_total: r.engage_views_total, engage_likes_total: r.engage_likes_total }));
    }).catch(() => { /* la lecture continue normalement */ });
  };

  // Like Engage : bascule atomique, anti-double-clic, total >= 0.
  const toggleEngageLike = () => {
    if (!ep?.source_id || liking) return;
    setLiking(true);
    engageLike(ep.source_id)
      .then((r) => setEngage((e) => ({ ...(e || {}), a_liked: r.a_liked, engage_likes_total: r.engage_likes_total, engage_views_total: r.engage_views_total ?? (e?.engage_views_total ?? 0) })))
      .catch(() => { /* silencieux */ })
      .finally(() => setLiking(false));
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) { await navigator.share({ title: ep?.titre || 'Citytoon', url }); return; }
      await navigator.clipboard.writeText(url);
      toast({ title: 'Lien copié', description: 'Partagez ce webtoon avec vos amis.' });
    } catch (_) { /* annulé */ }
  };

  if (error) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center" data-testid="fiche-unavailable">
      <p className="font-display text-xl mb-4">Ce webtoon est indisponible.</p>
      <Link to="/catalogue" className="text-primary font-800">Retour au catalogue</Link>
    </div>
  );
  if (!data) return <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const cover = ep.featured_image?.url;
  const fav = isFavorite(ep.source_id);
  const ctx = data.collection;

  return (
    <div data-testid="webtoon-fiche">
      {/* HERO */}
      <div className="relative overflow-hidden">
        {cover && <div className="absolute inset-0 h-80 overflow-hidden"><img src={cover} alt="" className="w-full h-full object-cover blur-2xl scale-110 opacity-40" /></div>}
        <div className="relative max-w-7xl mx-auto px-4 py-8">
          <button onClick={() => navigate(collection ? `/collection/${encodeURIComponent(collection)}` : -1)} data-testid="fiche-back-btn" className="inline-flex items-center gap-2 text-foreground font-700 mb-6 hover:text-primary transition-colors"><ArrowLeft className="w-4 h-4" /> Retour</button>
          <div className="grid md:grid-cols-[300px_1fr] gap-8">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white h-fit">
              {cover ? <img src={cover} alt={ep.titre} className="w-full aspect-square object-cover" data-testid="fiche-cover" /> : <div className="w-full aspect-square bg-muted" />}
              {isNew && <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-primary text-white text-xs font-800 uppercase shadow-lg">Nouveau</span>}
            </div>
            <div>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="fiche-badges">
                {(ep.collections || []).map((c, i) => (
                  <Link key={c.slug} to={`/collection/${encodeURIComponent(c.slug)}`} className="px-3 py-1 rounded-full text-xs font-800 uppercase text-white shadow-sm hover:opacity-90 transition-opacity" style={{ background: BADGE_COLORS[i % BADGE_COLORS.length] }}>{c.name}</Link>
                ))}
                {(ep.chapters || []).map((c) => (
                  <span key={c.slug} className="px-3 py-1 rounded-full text-xs font-800 uppercase bg-muted text-foreground/80">{c.name}</span>
                ))}
              </div>
              <h1 className="font-display font-700 text-3xl sm:text-4xl text-foreground mb-4">{ep.titre}</h1>

              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm font-700 text-muted-foreground mb-6">
                {(ep.locations || []).length > 0 && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary" /> {ep.locations.slice(0, 2).map((l) => l.name).join(', ')}</span>}
                {year && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-primary" /> {year}</span>}
                {imagesCount > 0 && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" /> ≈ {durationMin} min de lecture</span>}
              </div>

              {ctx && (
                <p className="text-sm font-700 mb-5" data-testid="fiche-collection-link">
                  <Layers className="w-4 h-4 inline text-secondary mr-1" /> Fait partie de la collection <Link to={`/collection/${encodeURIComponent(ctx.slug)}`} className="text-secondary font-800 hover:underline">{ctx.name}</Link> <span className="text-muted-foreground">· {ctx.position}/{ctx.total}</span>
                </p>
              )}

              {(ep.auteurs || []).length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-800 uppercase text-muted-foreground mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Auteurs et autrices</p>
                  <div className="flex flex-wrap gap-2">
                    {ep.auteurs.map((a) => (
                      <Link key={a.slug} to={`/auteur/${encodeURIComponent(a.slug)}`} className="flex items-center gap-2.5 rounded-full bg-muted hover:bg-secondary/20 pl-1 pr-4 py-1 transition-colors">
                        <AuthorAvatar author={a} size={34} /><span className="font-700 text-sm">{a.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {imagesCount === 0 && (
                <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-amber-800 font-700 text-sm" data-testid="fiche-no-images">
                  Les planches de ce webtoon ne sont pas encore disponibles.
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <div className="w-full flex items-center gap-3 flex-wrap text-sm font-700 text-muted-foreground" data-testid="fiche-stats" aria-label="Statistiques (cumul Citytoon.org + Engage)">
                    <button
                      type="button"
                      onClick={toggleEngageLike}
                      disabled={liking}
                      data-testid="fiche-like-btn"
                      aria-pressed={aLiked}
                      aria-label={aLiked ? 'Retirer mon j’aime' : 'Ajouter mon j’aime'}
                      className={`flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full border transition-all ${aLiked ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-muted border-transparent hover:border-primary/30 hover:text-primary'} disabled:opacity-60`}
                    >
                      <Heart className={`w-4 h-4 ${aLiked ? 'fill-primary' : ''}`} />
                      {likesDisplay !== null ? <span data-testid="fiche-likes-count">{fmtStat(likesDisplay)} j’aime</span> : <span>J’aime</span>}
                    </button>
                    {viewsDisplay !== null && (
                      <span className="flex items-center gap-1.5" data-testid="fiche-views-count" title="Vues cumulées">
                        <Eye className="w-4 h-4 text-primary" /> {fmtStat(viewsDisplay)} vues
                      </span>
                    )}
                  </div>
                {imagesCount > 0 ? (
                  <Link to={readLink} onClick={countEngageView} data-testid="fiche-read-btn" className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-white font-800 hover:scale-105 transition-transform shadow-lg shadow-primary/25 focus-visible:ring-4 focus-visible:ring-primary/40">
                    <Play className="w-5 h-5 fill-white" /> {started ? 'Continuer la lecture' : 'Commencer la lecture'}
                  </Link>
                ) : (
                  <Link to="/catalogue" className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-muted text-foreground font-800 hover:bg-secondary/20 transition-colors"><BookOpen className="w-5 h-5" /> Retour au catalogue</Link>
                )}
                <button onClick={() => toggleFavorite(ep.source_id)} data-testid="fiche-fav-btn" className={`flex items-center gap-2 px-6 py-3.5 rounded-full font-800 transition-all ${fav ? 'bg-amber-400 text-amber-950' : 'bg-muted text-foreground hover:bg-secondary/20'}`}>
                  <Heart className={`w-5 h-5 ${fav ? 'fill-amber-950' : ''}`} /> {fav ? 'Favori' : 'Ajouter aux favoris'}
                </button>
                <button onClick={share} data-testid="fiche-share-btn" className="flex items-center gap-2 px-6 py-3.5 rounded-full bg-secondary text-secondary-foreground font-800 hover:scale-105 transition-transform"><Share2 className="w-5 h-5" /> Partager</button>
              </div>

              {ep.source_url && <a href={ep.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-muted-foreground font-700 text-sm mt-5 hover:text-primary transition-colors"><ExternalLink className="w-4 h-4" /> Voir sur citytoon.org</a>}
            </div>
          </div>
        </div>
      </div>

      {/* À DÉCOUVRIR AUSSI */}
      {related.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 py-12">
          <h3 className="font-display font-700 text-2xl mb-6">À découvrir aussi</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {related.map((e) => (
              <Link key={e.source_id} to={`/fiche/${encodeURIComponent(e.slug)}`} data-testid={`fiche-related-${e.slug}`} className="card-hover group bg-card rounded-2xl overflow-hidden border border-border">
                <div className="aspect-square overflow-hidden">{e.featured_image?.url ? <img src={e.featured_image.url} alt={e.titre} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /> : <div className="w-full h-full bg-muted" />}</div>
                <div className="p-3"><h4 className="font-display font-600 text-sm line-clamp-2 group-hover:text-primary transition-colors">{e.titre}</h4></div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebtoonFiche;
